import { useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8fkg3k13ra8mig8qsg";

const contactInfo = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "hello@51dailyarena.com" },
  { icon: "ri-customer-service-2-line", label: "用户支持", value: "support@51dailyarena.com" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "上海市浦东新区体育中心 8 号" },
  { icon: "ri-time-line", label: "服务时间", value: "周一至周日 09:00 - 21:00" },
];

const socials = [
  { icon: "ri-wechat-line", label: "微信" },
  { icon: "ri-weibo-line", label: "微博" },
  { icon: "ri-bilibili-line", label: "哔哩哔哩" },
  { icon: "ri-youtube-line", label: "YouTube" },
];

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  usePageMeta(
    "联系我们 - 51每日大赛",
    "欢迎联系 51每日大赛，通过反馈表单留下您的建议与意见，或通过邮箱与我们取得联系。",
    "联系我们,反馈,合作咨询,51每日大赛",
  );
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("提交成功，感谢您的反馈，我们会尽快与您联系！");
      form.reset();
      setLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage("提交成功，感谢您的反馈，我们会尽快与您联系！");
        form.reset();
        setLength(0);
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">联系我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">联系我们</h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-3xl leading-relaxed">
            欢迎通过下方表单留下你的建议与反馈，我们会在收到后尽快回复。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg text-foreground-950 mb-1">发送反馈</h2>
              <p className="text-sm text-foreground-600 mb-6">带 * 的为必填项。</p>

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-field-optional"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入您的姓名"
                      className="w-full h-11 px-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full h-11 px-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm text-foreground-700 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    defaultValue="建议与反馈"
                    className="w-full h-11 px-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors cursor-pointer"
                  >
                    <option value="建议与反馈">建议与反馈</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    onChange={(e) => setLength(e.target.value.length)}
                    placeholder="请输入您想反馈的内容（最多 500 字）"
                    className="w-full px-4 py-3 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
                  />
                  <div className="mt-1 text-right text-xs text-foreground-500">{length}/500</div>
                </div>

                {status === "success" ? (
                  <div className="flex items-start gap-2 rounded-md bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-checkbox-circle-line text-lg" />
                    <span>{message}</span>
                  </div>
                ) : null}
                {status === "error" ? (
                  <div className="flex items-start gap-2 rounded-md bg-secondary-100 border border-background-300 px-4 py-3 text-sm text-secondary-900">
                    <i className="ri-error-warning-line text-lg" />
                    <span>{message}</span>
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 h-11 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> 提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" /> 提交反馈
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg text-foreground-950 mb-4">联系方式</h2>
              <ul className="space-y-4">
                {contactInfo.map((c) => (
                  <li key={c.label} className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                      <i className={`${c.icon} text-base`} />
                    </span>
                    <div>
                      <div className="text-xs text-foreground-500">{c.label}</div>
                      <div className="text-sm text-foreground-900 mt-0.5">{c.value}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg text-foreground-950 mb-4">关注我们</h2>
              <div className="flex items-center gap-3 flex-wrap">
                {socials.map((s) => (
                  <span
                    key={s.label}
                    title={s.label}
                    className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    <i className={`${s.icon} text-lg`} />
                  </span>
                ))}
              </div>
              <p className="mt-4 text-xs text-foreground-500 leading-relaxed">
                本站赛事与新闻信息均为演示内容，如有合作或内容纠错需求，欢迎通过表单联系我们。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}